import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Shield, Upload, Lock, Save, Camera } from 'lucide-react';
import { API_URL as CONFIG_API_URL } from '../../config';

const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

export default function ProfilePage() {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    avatar: '',
    groupName: '',
    quotaUsed: 0,
    quotaLimit: 0,
  });

  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passMessage, setPassMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setProfile({
          name: data.data.name || '',
          email: data.data.email || '',
          phone: data.data.phone || '',
          role: data.data.role || '',
          avatar: data.data.avatar || '',
          groupName: data.data.userGroup?.name || '无用户组',
          quotaUsed: data.data.userGroup?.quotaUsed || 0,
          quotaLimit: data.data.userGroup?.quotaLimit || 0,
        });
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profile.name,
          phone: profile.phone,
          avatar: profile.avatar
        })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '基本信息已更新' });
        // Update local storage name if needed
        localStorage.setItem('userName', profile.name);
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        setMessage({ type: 'error', text: data.error || '更新失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '网络错误' });
    }
  };

  const handlePasswordUpdate = async () => {
    setPassMessage({ type: '', text: '' });
    if (passwords.newPassword !== passwords.confirmPassword) {
      return setPassMessage({ type: 'error', text: '两次输入的新密码不一致' });
    }
    if (!passwords.oldPassword || !passwords.newPassword) {
      return setPassMessage({ type: 'error', text: '请填写完整的密码信息' });
    }

    try {
      const res = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oldPassword: passwords.oldPassword,
          newPassword: passwords.newPassword
        })
      });
      const data = await res.json();
      if (data.success) {
        setPassMessage({ type: 'success', text: '密码修改成功！下次请使用新密码登录' });
        setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPassMessage({ type: 'error', text: data.error || '密码修改失败' });
      }
    } catch (err) {
      setPassMessage({ type: 'error', text: '网络错误' });
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(`${API_URL}/images/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.url) {
        setProfile({ ...profile, avatar: data.url });
        setMessage({ type: 'success', text: '头像上传成功，请点击保存' });
      } else {
        setMessage({ type: 'error', text: '上传头像失败' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: '网络错误' });
    }
  };

  if (loading) return <div className="p-8">加载中...</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">个人设置</h1>
        <p className="text-gray-500 dark:text-slate-400 mt-1">管理您的个人资料和账号安全</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Basic Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-500" />
              基本资料
            </h2>
            
            <div className="flex flex-col md:flex-row gap-8">
              {/* Avatar Section */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white dark:border-slate-700 shadow-lg bg-gray-100 dark:bg-slate-900 flex items-center justify-center">
                    {profile.avatar ? (
                      <img src={API_URL.replace('/api', '') + profile.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-16 h-16 text-gray-400 dark:text-slate-500" />
                    )}
                  </div>
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-8 h-8 text-white" />
                  </div>
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleAvatarUpload}
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  更换头像
                </button>
              </div>

              {/* Form Section */}
              <div className="flex-1 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">用户名 / 昵称</label>
                  <input 
                    type="text" 
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:text-white transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">登录邮箱 (不可修改)</label>
                  <div className="flex items-center gap-2 w-full px-4 py-2 bg-gray-100 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-500 dark:text-slate-400">
                    <Mail className="w-4 h-4" />
                    <span>{profile.email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">手机号码</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="w-4 h-4 text-gray-400" />
                    </div>
                    <input 
                      type="text" 
                      autoComplete="new-password" value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:text-white transition-colors"
                      placeholder="选填"
                    />
                  </div>
                </div>

                {message.text && (
                  <div className={`p-3 text-sm rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                    {message.text}
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100 dark:border-slate-700 flex justify-end">
                  <button 
                    onClick={handleProfileUpdate}
                    className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                  >
                    <Save className="w-4 h-4" />
                    保存修改
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Security & Quota */}
        <div className="space-y-6">
          
          {/* Identity & Quota */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              身份与额度
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-50 dark:border-slate-700">
                <span className="text-sm text-gray-500 dark:text-slate-400">系统角色</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{profile.role === 'ADMIN' ? '超级管理员' : '普通用户'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50 dark:border-slate-700">
                <span className="text-sm text-gray-500 dark:text-slate-400">所属用户组</span>
                <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{profile.groupName}</span>
              </div>
              <div className="pt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-500 dark:text-slate-400">AI 算力额度</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {profile.quotaLimit === -1 ? '无限' : `${profile.quotaUsed} / ${profile.quotaLimit}`}
                  </span>
                </div>
                {profile.quotaLimit !== -1 && profile.quotaLimit > 0 && (
                  <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-indigo-500 h-2 rounded-full" 
                      style={{ width: `${Math.min((profile.quotaUsed / profile.quotaLimit) * 100, 100)}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Security */}
          {!profile.groupName?.startsWith('SSO-') ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-rose-500" />
                修改密码
              </h2>
              <div className="space-y-4">
                <div>
                  <input 
                    type="password" 
                    placeholder="当前密码"
                    autoComplete="new-password" value={passwords.oldPassword}
                    onChange={(e) => setPasswords({...passwords, oldPassword: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm dark:text-white"
                  />
                </div>
                <div>
                  <input 
                    type="password" 
                    placeholder="新密码"
                    autoComplete="new-password" value={passwords.newPassword}
                    onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm dark:text-white"
                  />
                </div>
                <div>
                  <input 
                    type="password" 
                    placeholder="确认新密码"
                    autoComplete="new-password" value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm dark:text-white"
                  />
                </div>

                {passMessage.text && (
                  <div className={`p-2 text-xs rounded-lg ${passMessage.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                    {passMessage.text}
                  </div>
                )}

                <button 
                  onClick={handlePasswordUpdate}
                  className="w-full py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  更新密码
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-gray-400" />
                安全与密码
              </h2>
              <div className="p-4 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                您的账号通过单点登录(SSO)授权，安全和密码由主系统统一管理，无需在此修改。
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
